import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Plus, Undo2, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import PageShell from '../components/PageShell';
import DataTable, { type Column } from '../components/DataTable';
import Tabs from '../components/Tabs';
import Modal from '../components/Modal';
import StatusPill from '../components/StatusPill';
import FilterBar, {
  controlClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../components/FilterBar';
import { formatCurrency, formatDate } from '../lib/format';
import {
  fetchPayPalTransactions,
  fetchPayPalOrders,
  syncPayPalTransactions,
  capturePayPalOrder,
  refundPayPalPayment,
  createPayPalInvoice,
} from '../lib/api';

type Row = Record<string, unknown>;
type TabId = 'transactions' | 'orders' | 'invoices';

function str(row: Row, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && value !== '') {
      if (typeof value === 'object') continue;
      return String(value);
    }
  }
  return null;
}

function nested(row: Row, path: string[]): string | null {
  let current: unknown = row;
  for (const key of path) {
    if (current && typeof current === 'object' && key in (current as Record<string, unknown>)) {
      current = (current as Record<string, unknown>)[key];
    } else {
      return null;
    }
  }
  if (current === null || current === undefined || typeof current === 'object') return null;
  return String(current);
}

function PayPal() {
  const [tab, setTab] = useState<TabId>('transactions');
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [acting, setActing] = useState<string | null>(null);
  const [invoiceOpen, setInvoiceOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const result =
      tab === 'orders'
        ? await fetchPayPalOrders('all', 50, 0)
        : await fetchPayPalTransactions(undefined, undefined, 50, 0);
    if (result.ok && result.data) {
      setRows(result.data.data ?? []);
    } else {
      setRows([]);
    }
    setLoading(false);
  }, [tab]);

  useEffect(() => {
    if (tab !== 'invoices') load();
    else setLoading(false);
  }, [tab, load]);

  const handleSync = async () => {
    setSyncing(true);
    const result = await syncPayPalTransactions({});
    if (result.ok) {
      const status = String((result.data as Row)?.status ?? 'ok');
      if (status === 'ok') toast.success('PayPal transactions synced');
      else toast(status === 'not_configured' ? 'PayPal is not configured' : `Sync: ${status}`);
      await load();
    }
    setSyncing(false);
  };

  const handleCapture = async (orderId: string) => {
    setActing(orderId);
    const result = await capturePayPalOrder(orderId);
    if (result.ok) {
      toast.success('Order captured');
      await load();
    }
    setActing(null);
  };

  const handleRefund = async (paymentId: string) => {
    setActing(paymentId);
    const result = await refundPayPalPayment(paymentId, {});
    if (result.ok) {
      toast.success('Refund submitted');
      await load();
    }
    setActing(null);
  };

  const transactionColumns: Column<Row>[] = [
    {
      key: 'date',
      header: 'Date',
      render: (r) =>
        formatDate(
          nested(r, ['transaction_info', 'transaction_initiation_date']) ??
            str(r, 'create_time', 'date')
        ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (r) =>
        nested(r, ['transaction_info', 'transaction_subject']) ??
        str(r, 'description', 'subject') ??
        '—',
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (r) => {
        const value =
          nested(r, ['transaction_info', 'transaction_amount', 'value']) ?? str(r, 'amount', 'value');
        const currency =
          nested(r, ['transaction_info', 'transaction_amount', 'currency_code']) ?? 'USD';
        return formatCurrency(value, currency);
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <StatusPill status={nested(r, ['transaction_info', 'transaction_status']) ?? str(r, 'status') ?? 'posted'} />
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => {
        const paymentId = str(r, 'id') ?? nested(r, ['transaction_info', 'transaction_id']);
        if (!paymentId) return null;
        return (
          <button
            type="button"
            onClick={() => handleRefund(paymentId)}
            disabled={acting === paymentId}
            className={secondaryButtonClass}
          >
            <Undo2 size={14} /> Refund
          </button>
        );
      },
    },
  ];

  const orderColumns: Column<Row>[] = [
    { key: 'id', header: 'Order ID', render: (r) => str(r, 'id', 'order_id') ?? '—' },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (r) =>
        formatCurrency(
          nested(r, ['purchase_units', '0', 'amount', 'value']) ?? str(r, 'amount', 'value'),
          nested(r, ['purchase_units', '0', 'amount', 'currency_code']) ?? 'USD'
        ),
    },
    { key: 'status', header: 'Status', render: (r) => <StatusPill status={str(r, 'status') ?? 'created'} /> },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => {
        const orderId = str(r, 'id', 'order_id');
        if (!orderId) return null;
        return (
          <button
            type="button"
            onClick={() => handleCapture(orderId)}
            disabled={acting === orderId}
            className={primaryButtonClass}
          >
            <CheckCircle2 size={14} /> Capture
          </button>
        );
      },
    },
  ];

  return (
    <PageShell
      title="PayPal"
      subtitle="PayPal transactions, orders, and invoices with capture and refund actions."
    >
      <Tabs
        tabs={[
          { id: 'transactions', label: 'Transactions' },
          { id: 'orders', label: 'Orders' },
          { id: 'invoices', label: 'Invoices' },
        ]}
        active={tab}
        onChange={(id) => setTab(id as TabId)}
      />

      {tab === 'invoices' ? (
        <div className="rounded-xl border border-zt-border bg-zt-surface p-6">
          <h3 className="font-semibold text-white">Create a PayPal invoice</h3>
          <p className="mt-1 text-sm text-zt-muted">
            Draft and send an invoice directly through PayPal. It is created as a draft and then sent
            to the recipient.
          </p>
          <button
            type="button"
            onClick={() => setInvoiceOpen(true)}
            className={`mt-4 ${primaryButtonClass}`}
          >
            <Plus size={16} /> New invoice
          </button>
        </div>
      ) : (
        <>
          <FilterBar
            actions={
              tab === 'transactions' ? (
                <button type="button" onClick={handleSync} disabled={syncing} className={primaryButtonClass}>
                  <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} /> Sync now
                </button>
              ) : (
                <button type="button" onClick={load} className={secondaryButtonClass}>
                  <RefreshCw size={16} /> Refresh
                </button>
              )
            }
          >
            <span className="text-sm text-zt-muted">
              {tab === 'transactions' ? 'Recent PayPal transactions' : 'PayPal orders'}
            </span>
          </FilterBar>

          <DataTable
            columns={tab === 'orders' ? orderColumns : transactionColumns}
            rows={rows}
            rowKey={(r, i) => str(r, 'id', 'order_id') ?? nested(r, ['transaction_info', 'transaction_id']) ?? String(i)}
            loading={loading}
            emptyTitle={tab === 'orders' ? 'No orders' : 'No transactions'}
            emptyMessage="Connect PayPal and sync to see data here."
          />
        </>
      )}

      <CreateInvoiceModal open={invoiceOpen} onClose={() => setInvoiceOpen(false)} />
    </PageShell>
  );
}

function CreateInvoiceModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [email, setEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setEmail('');
    setAmount('');
    setCurrency('USD');
    setNote('');
  };

  const handleSubmit = async () => {
    if (!email.trim() || !amount.trim()) {
      toast.error('Recipient email and amount are required');
      return;
    }
    setSubmitting(true);
    const result = await createPayPalInvoice({
      recipient_email: email.trim(),
      amount: amount.trim(),
      currency,
      note: note.trim() || undefined,
    });
    setSubmitting(false);
    if (result.ok) {
      toast.success('PayPal invoice created');
      reset();
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New PayPal invoice"
      footer={
        <>
          <button type="button" onClick={onClose} className={secondaryButtonClass}>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={submitting} className={primaryButtonClass}>
            {submitting ? 'Creating…' : 'Create & send'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-200">Recipient email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="client@example.com"
            className={`w-full ${controlClass}`}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-200">Amount</label>
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="100.00"
              className={`w-full ${controlClass}`}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-200">Currency</label>
            <input
              type="text"
              value={currency}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
              className={`w-full ${controlClass}`}
            />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-200">Note (optional)</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            className={`w-full ${controlClass}`}
          />
        </div>
      </div>
    </Modal>
  );
}

export default PayPal;
